import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useComponentIsUnmounted } from "./useComponentIsUnmounted";
import { useDeferredEffect } from "./useDeferredEffect";
import { useThrottledCallback } from "./useThrottledCallback";
import { useIsIOSWebkit } from "./useIsIOSWebkit";

import { setAffixHeaderOffsetTop } from "../../reducers/styling";

export const useAffixHeaderOffsetTopUpdater = (callback, dependencies) => {
  const isUnmounted = useComponentIsUnmounted();
  const dispatch = useDispatch();

  const affixHeaderOffsetTop = useSelector(({ styling }) => styling.affixHeaderOffsetTop);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const calculateNewAffixHeaderOffsetTop = useCallback(callback, dependencies);

  const isIOSWebkit = useIsIOSWebkit();

  const updateAffixHeaderOffsetTop = useThrottledCallback(
    () => {
      const newOffsetTop = calculateNewAffixHeaderOffsetTop();

      // Do not process any null or undefined value to allow the provided callback to
      // return when it can't or doesn't need to calculate a new value.
      if (newOffsetTop === null || newOffsetTop === void 0) return;
      // Avoid running an update loop here
      if (affixHeaderOffsetTop !== newOffsetTop) {
        dispatch(setAffixHeaderOffsetTop(newOffsetTop));
      }
    },
    [affixHeaderOffsetTop, calculateNewAffixHeaderOffsetTop, dispatch],
    50,
    { leading: false }
  );

  // Ensure that we update the affix header top offset when the window is resized
  useEffect(() => {
    if (!isIOSWebkit) {
      window.addEventListener("scroll", updateAffixHeaderOffsetTop);
    }

    window.addEventListener("resize", updateAffixHeaderOffsetTop);

    return () => {
      if (!isIOSWebkit) {
        window.removeEventListener("scroll", updateAffixHeaderOffsetTop);
      }

      window.removeEventListener("resize", updateAffixHeaderOffsetTop);
    };
  }, [isIOSWebkit, updateAffixHeaderOffsetTop]);

  useDeferredEffect(
    () => {
      if (isUnmounted()) return;

      updateAffixHeaderOffsetTop();
    },
    [isUnmounted, updateAffixHeaderOffsetTop],
    { cleanupCallback: updateAffixHeaderOffsetTop }
  );

  return { affixHeaderOffsetTop, updateAffixHeaderOffsetTop };
};
